/**
 * @Description This is FASAT Quick Action Notification component
 * @FileName fasatToast.js
 * @Author Imroz Dawar-dawarim
 * @CreatedOn 9 February, 2021 15:57:14
 * @IssueID CDTFASATMOD-314
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Alert } from '@manulife/mux';
import { FasatIcon } from '../index';
import './fasatToast.scss';

/**
 * This would close the existing fasatQuickActionNotification component
 * has not been exported.
 * @function useEnterAlerter : used to close toast if enter/esc/spacebar is pressed
 *
 */
const keyCodeTwentySeven = 27;
const keyCodeThirtyTwo = 32;
const useEnterAlerter = (ref) => {
  useEffect(() => {
    const listener = (event) => {
      if (
        event.keyCode === keyCodeTwentySeven
        || event.code === 'Enter'
        || event.keyCode === keyCodeThirtyTwo
        || event.code === 'NumpadEnter'
      ) {
        toast.dismiss();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [ref]);
};

/**
 * This would return the fasat specific toast message styling. Hence this component
 * has not been exported.
 * @param {String} message : message which would be shown in the toast
 * @param {string} variantType : used to change the MUX component imported
 */
export function FasatToastContent({ message, variantType }) {
  const toastRef = useRef(0);

  useEnterAlerter(toastRef);
  return (
    <div className="messageContainer" ref={toastRef}>
      <span className="iconContainer">
        <Alert
          closeIcon={(
            <FasatIcon
              alt=""
              ariaHidden
              cdsName=""
              color="inherit"
              fill="none"
              fontsize="21px"
              height={null}
              manulifeName="close"
              name=""
              opacity={1}
              path=""
              secondaryColor="none"
              width="70%"
            />
          )}
          closeIconAriaLabel=""
          hasCloseButton
          titleText="Important Information"
          // Accessibility: Use h2 for proper heading hierarchy since toast has content under the title
          titleAs="h2"
          variant={variantType}
        >
          {typeof message === 'object' && message.map((msg) => <div key={msg}>{msg}</div>)}
          {typeof message === 'string' && <div>{message}</div>}
        </Alert>
      </span>
    </div>
  );
}
FasatToastContent.propTypes = {
  variantType: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
};

/**
 * This would show the message via FasatToastContainer.
 * Please note that you have to include FasatToastContainer
 * to make this function work.
 * @param {String} message - message which needs to be shown as toast message
 * @param {*} option - options to configure the toast
 */
const FasatShowToastMessage = (
  message,
  variantType,
  option = {
    position: 'bottom-center',
    autoClose: false,
    hideProgressBar: true,
    closeOnClick: true,
    closeButton: false,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    className: 'fasatQuickActionNotification',
  },
) => {
  toast(<FasatToastContent message={message} variantType={variantType} />, option);
};

export default FasatShowToastMessage;
